import React from 'react';
import { View, Text } from '@react-pdf/renderer';
import { type, colors, space, statusPalette } from '../theme';
import Eyebrow from '../components/Eyebrow';
import CheckXItem from '../components/CheckXItem';

// Matches the in-app "Murphy review" block — the teal banner + grade dot at
// the top, then the Positives / Areas For Improvement two-column grid. Q&A
// Breakdown follows in its own section.
const MurphyReviewSection = ({ report }) => {
  const review = report.murphyReview;
  if (!review) return null;
  const palette = statusPalette[review.statusColor] || statusPalette.gray;
  const covered = review.covered || [];
  const missing = review.missing || [];
  // Neutral delivery note (qa-family delivery read): printed under the grid,
  // never as a positive or an improvement (Toby, 2026-09-04).
  const delivery = review.delivery || [];

  return (
    <View>
      {/* Teal banner + grade row stay together via wrap={false} — the section
          header should never appear alone at the bottom of a page. */}
      <View wrap={false}>
        <View style={{
          backgroundColor: colors.teal,
          paddingVertical: 8,
          paddingHorizontal: space.md,
          borderRadius: 4,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Text style={{
            fontFamily: 'Montserrat',
            fontWeight: 600,
            fontSize: 9.5,
            color: '#ffffff',
          }}>
            Review created by Murphy the MultiplyAI agent
          </Text>
        </View>

        {/* Grade indicator row */}
        <View style={{
          flexDirection: 'row',
          alignItems: 'center',
          marginTop: space.md,
          gap: space.md,
        }}>
        <View style={{ flex: 1 }}>
          <Text style={[type.micro, { color: colors.muted, textTransform: 'uppercase', letterSpacing: 0.4 }]}>
            Type of Sales Message
          </Text>
          <Text style={[type.bodyMed, { color: colors.ink, marginTop: 4 }]}>Live Meeting</Text>
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.sm }}>
          <View style={{ width: 14, height: 14, borderRadius: 7, backgroundColor: palette.fg }} />
          <Text style={[type.bodyMed, { color: colors.ink }]}>
            {review.statusLabel}
          </Text>
        </View>
        </View>
      </View>

      {/* Positives / Areas For Improvement grid */}
      <View style={{ flexDirection: 'row', gap: space.lg, marginTop: space.md }}>
        <View style={{ flex: 1 }}>
          <Eyebrow>Positives · {covered.length}</Eyebrow>
          <View style={{ marginTop: space.sm }}>
            {covered.length === 0
              ? <Text style={[type.caption, { color: colors.muted }]}>—</Text>
              : covered.map((c, i) => <CheckXItem key={i} kind="check">{c}</CheckXItem>)}
          </View>
        </View>
        <View style={{ flex: 1 }}>
          <Eyebrow>Areas For Improvement · {missing.length}</Eyebrow>
          <View style={{ marginTop: space.sm }}>
            {missing.length === 0
              ? <Text style={[type.caption, { color: colors.muted }]}>—</Text>
              : missing.map((m, i) => <CheckXItem key={i} kind="x">{m}</CheckXItem>)}
          </View>
        </View>
      </View>

      {delivery.length > 0 && (
        <View style={{ marginTop: space.md }} wrap={false}>
          <Eyebrow>Delivery</Eyebrow>
          <View style={{ marginTop: space.sm }}>
            {delivery.map((d, i) => (
              <Text key={i} style={[type.body, { color: colors.ink, marginBottom: 4 }]}>{d}</Text>
            ))}
          </View>
        </View>
      )}
    </View>
  );
};

export default MurphyReviewSection;
